import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDataProvider } from 'react-admin';
import { closeDoc, openDoc, updateDoc } from 'store/actions/lightboxActions';
import useCustomNotify from 'hooks/useCustomNotify';

const usePrintLabel = ({
  id,
  isReprint = false,
  returnLabel = false,
  trackingNumber,
}: {
  id?: string;
  isReprint?: boolean;
  returnLabel?: boolean;
  trackingNumber?: string;
}) => {
  const [printingLabel, setPrintingLabel] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useCustomNotify();
  const dispatch = useDispatch();

  const getPreferences = async () => {
    try {
      const { data: preferences } = await dataProvider.get('preferencesSettings');
      return preferences;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred while getting preferences';
      notify(errorMessage, 'error');
      return {};
    }
  };

  const onPrintLabel = async (
    printLabelAndPackingSlipTogether,
    localId,
    localTrackingNumber: string | undefined,
    loadingDocumentId: string
  ) => {
    const {
      data: { url, noticeHtml },
    } = await dataProvider.getNest(
      printLabelAndPackingSlipTogether && !isReprint && !returnLabel
        ? 'labelInfoAndPackingSlip'
        : 'labelInfo',
      { id: id || localId }
    );
    const resolvedTrackingNumber = localTrackingNumber || trackingNumber;

    dispatch(
      updateDoc(loadingDocumentId, {
        url,
        noticeHtml,
        trackingNumbers: resolvedTrackingNumber ? [resolvedTrackingNumber] : [],
        loading: false,
      })
    );
  };

  const printLabel = async (localId?: any, localTrackingNumber?: string) => {
    if (printingLabel) {
      return;
    }

    setPrintingLabel(true);

    const resolvedTrackingNumber = localTrackingNumber || trackingNumber;
    const loadingDocument = openDoc({
      name: 'Shipment Label',
      documentType: 'shipmentLabel',
      labelMode: 'single',
      trackingNumbers: resolvedTrackingNumber ? [resolvedTrackingNumber] : [],
      loading: true,
    });

    dispatch(loadingDocument);

    try {
      const { printLabelAndPackingSlipTogether } = await getPreferences();
      await onPrintLabel(
        printLabelAndPackingSlipTogether,
        localId,
        localTrackingNumber,
        loadingDocument.payload.id
      );
    } catch (err) {
      dispatch(closeDoc(loadingDocument.payload.id));
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred while printing label';
      notify(errorMessage, 'error');
    } finally {
      setPrintingLabel(false);
    }
  };

  return { printingLabel, printLabel };
};

export default usePrintLabel;
